import React from "react";
import {
  FileText,
  LineChart,
  PieChart,
  Workflow,
  ArrowUpRight,
  Landmark,
  Building2,
  TrendingUp,
} from "lucide-react";

const services = [
  {
    key: "soa",
    title: "SOA & ROA Drafting",
    description:
      "Audit ready Statements of Advice and Records of Advice drafted directly into Xplan, Xtools+ or Adviser Logic, meeting strict BID and RG90 standards. Plain English client narratives, every time.",
    bullets: ["Xplan / Xtools+ / Adviser Logic", "BID + RG90 compliant", "Audit ready formatting"],
    icon: FileText,
    span: "md:col-span-2",
    tone: "yellow",
  },
  {
    key: "strategy",
    title: "Strategy Modeling",
    description:
      "Super contribution, pension, debt recycling, salary sacrifice and retirement modeling in Xtools+, with audit trail logic you can defend in any client meeting.",
    bullets: ["Xtools+ projections", "Super & pension strategies", "Scenario comparisons"],
    icon: LineChart,
    span: "",
    tone: "white",
  },
  {
    key: "portfolio",
    title: "Portfolio Construction",
    description:
      "APL-aligned investment recommendations with asset allocation, risk-profile mapping, fee analysis and clean rationale your clients actually understand.",
    bullets: ["APL-aligned recommendations", "Risk-profile mapping", "Transparent fee analysis"],
    icon: PieChart,
    span: "",
    tone: "white",
  },
  {
    key: "automation",
    title: "AI Powered Workflow Automation",
    description:
      "I orchestrate multiple AI models alongside Excel VBA, Power Automate and modern no code tools to remove the manual steps that introduce errors. Cross model validation, smart Fact Find ingestion, and automated document assembly. Built to your practice, tuned to your licensee.",
    bullets: ["Multiple AI models, cross checked", "Excel VBA &amp; Power Automate", "Document assembly pipelines"],
    icon: Workflow,
    span: "md:col-span-2",
    tone: "dark",
  },
];

const suite = [
  { icon: Landmark, title: "Super, Pensions & Rollovers", body: "Standard and complex advice documents covering TTR and pension commencement strategies." },
  { icon: PieChart, title: "Investment Portfolios", body: "Establishing and managing direct investment accounts with APL-aligned product research." },
  { icon: Building2, title: "Family Trusts", body: "Comprehensive structure and strategy for sophisticated multi-generational wealth." },
  { icon: TrendingUp, title: "SMSFs & LRBAs", body: "Complex fund setup, contributions, compliance modeling, and limited recourse borrowing." },
  { icon: Workflow, title: "Gearing & Margin Lending", body: "Advanced debt-recycling and geared investment strategies for sophisticated clients." },
  { icon: FileText, title: "ROAs & Reviews", body: "Ongoing client reviews, fee renewals and Records of Advice at scale." },
];

const ServiceCard = ({ s }) => {
  const Icon = s.icon;
  const base = "relative border-2 border-black p-8 md:p-10 hover-lift transition-all";
  const toneClass =
    s.tone === "yellow"
      ? "bg-[#FACC15] text-black shadow-brutal"
      : s.tone === "dark"
      ? "bg-black text-white shadow-brutal-yellow"
      : "bg-white text-black shadow-brutal";

  return (
    <article
      data-testid={`service-card-${s.key}`}
      className={`${base} ${toneClass} ${s.span}`}
    >
      <div className="flex items-start justify-between">
        <div
          className={`w-12 h-12 border-2 ${
            s.tone === "dark" ? "border-white bg-[#FACC15]" : "border-black bg-white"
          } flex items-center justify-center`}
        >
          <Icon className="w-6 h-6 text-black" />
        </div>
        <ArrowUpRight className={`w-5 h-5 ${s.tone === "dark" ? "text-[#FACC15]" : "text-black"}`} />
      </div>
      <h3 className="font-display font-black mt-6 text-2xl md:text-3xl leading-tight tracking-tight">
        {s.title}
      </h3>
      <p className={`mt-3 text-sm md:text-base leading-relaxed ${s.tone === "dark" ? "text-white/80" : "text-black/80"}`}>
        {s.description}
      </p>
      <ul className="mt-6 space-y-2">
        {s.bullets.map((b) => (
          <li key={b} className="flex items-center gap-2 text-xs md:text-sm font-semibold">
            <span
              className={`inline-block w-2 h-2 ${
                s.tone === "dark" ? "bg-[#FACC15]" : "bg-black"
              }`}
            />
            {b}
          </li>
        ))}
      </ul>
    </article>
  );
};

const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-white border-b-2 border-black"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#FACC15] border-2 border-black px-3 py-1 text-xs font-bold uppercase tracking-widest shadow-brutal">
              Services
            </span>
            <h2 className="font-display font-black mt-4 text-4xl md:text-5xl tracking-tighter leading-[1.05]">
              Four pillars. One outsourced engine for your advice practice.
            </h2>
          </div>
          <p className="text-sm md:text-base text-black/70 max-w-md">
            Pick what you need, or hand over the whole engagement. Every output is QA&apos;d,
            version controlled, and delivered against your terms.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {services.map((s) => (
            <ServiceCard key={s.key} s={s} />
          ))}
        </div>

        {/* Strategy Suite */}
        <div className="mt-20 md:mt-28" data-testid="strategy-suite">
          <div className="max-w-2xl">
            <span className="inline-block bg-black text-white border-2 border-black px-3 py-1 text-xs font-bold uppercase tracking-widest">
              Strategy Suite
            </span>
            <h3 className="font-display font-black mt-4 text-3xl md:text-4xl tracking-tighter leading-[1.05]">
              From simple rollovers to SMSF mastery.
            </h3>
            <p className="mt-4 text-sm md:text-base text-black/70 max-w-md">
              Direct Xplan and Xtools+ capability across every advice requirement Australian
              practices encounter.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {suite.map((it) => {
              const Icon = it.icon;
              return (
                <div
                  key={it.title}
                  data-testid={`suite-${it.title.split(' ')[0].toLowerCase()}`}
                  className="bg-white border-2 border-black p-6 shadow-brutal hover-lift"
                >
                  <div className="w-10 h-10 bg-[#FACC15] border-2 border-black flex items-center justify-center">
                    <Icon className="w-5 h-5 text-black" />
                  </div>
                  <h4 className="font-display font-bold text-lg mt-4">{it.title}</h4>
                  <p className="mt-2 text-sm text-black/70 leading-relaxed">{it.body}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 inline-flex items-center gap-2 bg-[#FACC15] border-2 border-black px-4 py-3 shadow-brutal">
            <span className="w-2 h-2 bg-black" />
            <span className="text-xs md:text-sm font-bold uppercase tracking-wide">
              100% compliant documentation. BID &amp; RG90 standards, guaranteed.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
